// src/components/TraceIcons.jsx
//
// Icons for the Image-tracing toolbar. Inline SVG like ViewIcons.jsx — no icon
// dependency, sized in `em` so they follow the button's font-size, and drawn in
// `currentColor` so the active state's colour carries through with no extra rule.
//
// Every one of these had to say what it does to a POLYGON, not to a document.
// The generic stationery metaphors were rejected for that reason: scissors for
// Split says "cut this drawing out", and a floppy disk for Save says nothing at
// all to anyone who has not used one. What is drawn instead is the operation's
// effect on a room rectangle, which is the thing on screen underneath.
import React from 'react';

const S = ({ children }) => (
  <svg viewBox="0 0 24 24" width="1.15em" height="1.15em" fill="none"
       stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"
       strokeLinejoin="round" aria-hidden="true" focusable="false"
       style={{ display: 'block' }}>
    {children}
  </svg>
);

/** Arrow cursor over a corner handle — the tool that drags rooms and vertices. */
export const IconSelect = () => (
  <S>
    <path d="M5 3.5 17.5 12.5l-5.2.7 2.9 6-2.4 1.1-2.8-5.9-3.8 3.6Z" />
  </S>
);

/** An edge with a new vertex being inserted into its middle. */
export const IconAddPoint = () => (
  <S>
    <path d="M3.5 17.5 20.5 6" />
    <circle cx="12" cy="11.8" r="2.4" fill="currentColor" stroke="none" />
    <path d="M18.5 17.5h5M21 15v5" />
  </S>
);

/** One rectangle with a cut running through it — two rooms where there was one. */
export const IconSplit = () => (
  <S>
    <rect x="3.5" y="5.5" width="17" height="13" rx="1.5" />
    <path d="M12 3.5v17" strokeDasharray="2.6 2.4" />
  </S>
);

/** Two rectangles closing on the wall between them. */
export const IconMerge = () => (
  <S>
    <rect x="2.5" y="6.5" width="7.5" height="11" rx="1.4" />
    <rect x="14" y="6.5" width="7.5" height="11" rx="1.4" />
    <path d="M11.4 12h1.2M9.2 9.5 11.6 12l-2.4 2.5M14.8 9.5 12.4 12l2.4 2.5" />
  </S>
);

/**
 * A rectangle being drawn: dashed, because it is not there yet, with a plus for
 * the thing being added. Solid corners would read as an existing room.
 */
export const IconRectangle = () => (
  <S>
    <rect x="2.5" y="6" width="13" height="12" rx="1.4" strokeDasharray="2.8 2.2" />
    <path d="M19 4.5v7M15.5 8h7" />
  </S>
);

/** Arrow doubling back to the left. */
export const IconUndo = () => (
  <S>
    <path d="M3.5 8.5h11a5.5 5.5 0 0 1 0 11H8" />
    <path d="M7 4 3 8.5 7 13" />
  </S>
);

/** The same, mirrored. */
export const IconRedo = () => (
  <S>
    <path d="M20.5 8.5h-11a5.5 5.5 0 0 0 0 11H16" />
    <path d="M17 4l4 4.5-4 4.5" />
  </S>
);

/** Waste bin with a lid — the one place a stationery metaphor is universal. */
export const IconDelete = () => (
  <S>
    <path d="M4 6.5h16M9.5 6.5V4h5v2.5" />
    <path d="M6 6.5 7 20.5h10l1-14" />
    <path d="M10 10v7M14 10v7" />
  </S>
);

/** A tick landing on a rectangle: these rooms, kept. */
export const IconSave = () => (
  <S>
    <path d="M20.5 11V6.2a1.7 1.7 0 0 0-1.7-1.7H5.2A1.7 1.7 0 0 0 3.5 6.2v11.6a1.7 1.7 0 0 0 1.7 1.7h6" />
    <path d="M13.5 16.5 16 19l5-5.5" />
  </S>
);

/** Spinner ring for the moment a save is in flight. */
export const IconSaving = () => (
  <S>
    <path d="M12 3.5a8.5 8.5 0 1 1-8.5 8.5" />
  </S>
);
